import { Link } from "react-router-dom";
import LegalPage, { LegalSection } from "@/pages/legal/LegalPage";

const EFFECTIVE_DATE = "February 5, 2026";

export default function Privacy() {
  return (
    <LegalPage
      testId="privacy-page"
      eyebrow="Legal"
      title="Privacy Policy"
      effectiveDate={EFFECTIVE_DATE}
    >
      <p>
        This Privacy Policy explains what personal data PostShifted collects, how we use it, who
        we share it with, and the choices you have. It applies to your use of the PostShifted
        website and application (the &ldquo;<strong>Service</strong>&rdquo;).
      </p>

      <LegalSection n={1} title="Who we are">
        <p>
          The Service is operated by <strong>PostShifted</strong>. For any privacy question or request, contact us at{" "}
          <a href="mailto:support@postshifted.com" className="text-coral hover:text-coral-hover">
            support@postshifted.com
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection n={2} title="What we collect">
        <p>We collect only what we need to provide the Service:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            <strong>Account data.</strong> Email address, name, hashed password, and optionally a
            profile picture (if you sign in with Google).
          </li>
          <li>
            <strong>Content you submit.</strong> The text you paste in for repurposing, your brand
            voice samples, your saved templates, and your generated outputs.
          </li>
          <li>
            <strong>Usage data.</strong> Number of remixes generated, formats chosen, timestamps,
            and basic diagnostics (errors, latency) used to keep the Service healthy.
          </li>
          <li>
            <strong>Billing data.</strong> If you subscribe to Pro, our payment processor PayPal
            handles your payment method. PostShifted only stores non-sensitive fields returned by
            PayPal — subscription id, plan id, status, and renewal date. We never see or store
            your card number.
          </li>
          <li>
            <strong>Cookies.</strong> A single session cookie or local-storage token to keep you
            signed in. We do not use third-party ad tracking.
          </li>
        </ul>
      </LegalSection>

      <LegalSection n={3} title="How we use your data">
        <p>We use your data to:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>Operate the Service and generate the outputs you requested.</li>
          <li>Send transactional emails such as receipts, security alerts, and product changes.</li>
          <li>Enforce plan limits and prevent abuse.</li>
          <li>Improve the product (aggregated, non-identifying analytics only).</li>
          <li>Comply with legal obligations.</li>
        </ul>
        <p>
          We do <strong>not</strong> sell your personal data. We do <strong>not</strong> use your
          submitted content to train our own or third-party AI models.
        </p>
      </LegalSection>

      <LegalSection n={4} title="Sub-processors">
        <p>To provide the Service we rely on the following third parties:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            <strong>Anthropic</strong> (AI model provider) — receives the text you submit for the
            duration of the request in order to generate outputs.
          </li>
          <li>
            <strong>PayPal</strong> (payment processor) — handles Pro checkout, recurring billing,
            and any refunds. Governed by PayPal&apos;s own privacy policy.
          </li>
          <li>
            <strong>MongoDB Atlas</strong> (managed database hosting) — stores your account, brand
            voices, templates, and generation history.
          </li>
          <li>
            <strong>Emergent</strong> (application hosting) — hosts the web and API servers.
          </li>
        </ul>
        <p>
          Each sub-processor is bound by their own terms and privacy policies and processes data
          only as needed to deliver their portion of the Service.
        </p>
      </LegalSection>

      <LegalSection n={5} title="AI providers and your content">
        <p>
          When you request a remix, the text you submit is sent to Anthropic&apos;s Claude API for
          processing. Anthropic&apos;s current API terms state that API inputs and outputs are not
          used to train their models. We do not retain any additional copy of your input at
          Anthropic beyond the transient processing window.
        </p>
        <p>
          Please avoid pasting highly sensitive personal data (medical records, government IDs,
          etc.) into the Service. Generative AI is not designed as a store for that kind of data.
        </p>
      </LegalSection>

      <LegalSection n={6} title="Data retention">
        <p>
          We keep your account data and generation history for as long as your account exists.
          When you delete your account, all associated personal data (account, brand voices,
          templates, history, ideas) is deleted within 30 days, except where we are legally
          required to retain it (e.g. financial records for tax purposes).
        </p>
      </LegalSection>

      <LegalSection n={7} title="Your rights">
        <p>
          Depending on where you live, you may have rights under laws such as the EU/UK GDPR or
          the California CCPA, including:
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li>The right to access the personal data we hold about you.</li>
          <li>The right to correct or update inaccurate data.</li>
          <li>The right to delete your data (&ldquo;right to be forgotten&rdquo;).</li>
          <li>The right to object to or restrict certain processing.</li>
          <li>The right to receive your data in a portable format.</li>
          <li>The right to withdraw consent where processing is based on consent.</li>
        </ul>
        <p>
          To exercise any of these rights, email{" "}
          <a href="mailto:support@postshifted.com" className="text-coral hover:text-coral-hover">
            support@postshifted.com
          </a>{" "}
          from the address on your account. We will respond within 30 days.
        </p>
      </LegalSection>

      <LegalSection n={8} title="Security">
        <p>
          We use industry-standard security practices: HTTPS/TLS in transit, encryption at rest via
          our database provider, hashed and salted passwords (bcrypt), scoped API keys, and short-
          lived signed tokens. No system is 100% secure — please use a strong, unique password and
          notify us immediately if you suspect unauthorized access.
        </p>
      </LegalSection>

      <LegalSection n={9} title="International data transfers">
        <p>
          The Service is operated globally; your data may be processed in countries other than
          your own, including the United States and the European Union. Where required, we rely on
          appropriate safeguards such as the Standard Contractual Clauses.
        </p>
      </LegalSection>

      <LegalSection n={10} title="Children">
        <p>
          The Service is not directed at children under 16. We do not knowingly collect data from
          children. If you believe a child has provided us with personal data, please contact us so
          we can delete it.
        </p>
      </LegalSection>

      <LegalSection n={11} title="Changes to this policy">
        <p>
          We may update this Privacy Policy occasionally. Material changes will be announced in-app
          or by email at least 14 days before they take effect. The &ldquo;Effective date&rdquo; at
          the top of this page always reflects the latest version.
        </p>
      </LegalSection>

      <LegalSection n={12} title="Contact">
        <p>
          Privacy questions or data-subject requests:{" "}
          <a href="mailto:support@postshifted.com" className="text-coral hover:text-coral-hover">
            support@postshifted.com
          </a>
          .
        </p>
      </LegalSection>

      <p className="pt-4 text-sm text-neutral-500">
        See also our{" "}
        <Link to="/terms" className="text-coral hover:text-coral-hover">Terms of Service</Link>{" "}
        and{" "}
        <Link to="/refund" className="text-coral hover:text-coral-hover">Refund Policy</Link>.
      </p>
    </LegalPage>
  );
}
